/**
 * SecondChannel — duplex v2's side-voice (docs/duplex-v2.md): backchannels, fillers and bridges in <300ms while the main
 * agent's history stays clean. Lines go to the host's `speak`/`show` only — never to an Agent.
 *
 * Hot path: a rule gate decides at 0ms and a canned bank speaks at 0ms. The decider (Jev via Instinct.channel) only
 * REFINES a candidate the rule already proposed, inside `decisionBudgetMs`; a late answer is dropped (measured Jev
 * listen latency on real partials: p50 ~350ms, p95 ~800ms — too slow to lead, tests/voice-jev-bench.test.ts).
 *
 * Channel-1 (the main answer) lifecycle via onAgentState: a bridge only masks a SLOW start — it waits `bridgeDelayMs` and
 * plays only while channel-1 has produced nothing yet (its first token or audio drops it); once channel-1 responds no new
 * line starts. STACK, DON'T CUT: an in-flight line drains to its end — hosts queue channel-1 audio
 * behind `drain()` (gapless); only the user's barge-in (onBargeIn) cuts a line.
 */
import { forComponent } from '../logging';
import { CHANNEL_ACTIONS, type ChannelKind } from './instinct';
import type { ChatLike, ChatResponse } from '../llm';
import { cleanLine, detectLanguage, EN_BANK, isPhatic, languageName, norm, primaryLang, type ChannelBanks } from './channelLang';

const log = forComponent('SecondChannel');

export type LineKind = Exclude<ChannelKind, 'silence'>;
/** The main (channel-1) response lifecycle. `idle` = no turn in flight (the user has the floor). */
export type MainState = 'idle' | 'queued' | 'thinking' | 'streaming_text' | 'first_audio' | 'speaking';
type Trigger = 'interim' | 'pause' | 'working';

/** The micro-context a generator gets: a tiny tail, never history. */
export interface ChannelContext {
  kind: LineKind;
  /** The last ~microWords words the user said. */
  heard: string;
  state: MainState;
  /** The last few channel lines — do not reuse. */
  avoid: string[];
  /** The user's language (BCP-47 primary code) — the line is written in it. */
  lang: string;
}

export interface ChannelEvent {
  trigger: Trigger;
  rule: ChannelKind;
  choice?: string;
  acted: 'spoken' | 'silence' | 'stale' | 'gated' | 'skipped' | 'no_bank';
  kind?: LineKind;
  text?: string;
  ms: number;
}

export class SecondChannelOptions {
  /** Refiner (Jev/fast LLM): the choice over CHANNEL_ACTIONS, or null on failure. Absent → the rule alone decides. */
  decide?: (heard: string, state: Record<string, unknown>) => Promise<{ choice: string } | null>;
  /** Fast-model line writer (see fastLineGenerator). Absent or late → the canned bank. */
  generate?: (ctx: ChannelContext, signal: AbortSignal) => Promise<string>;
  /** Per-language banks (ChannelBanks). Absent → the hardcoded English bank, and silence in any other language. */
  banks?: Pick<ChannelBanks, 'get' | 'ensure'>;
  /** The host's language (BCP-47; pinned — detection never overrides it). Absent → unknown until the user's words
   *  tell it confidently; the channel is silent until then (never English by default). */
  lang?: string;
  /** Kinds worth generating (content-conditioned); the rest are canned sounds. */
  generateFor: LineKind[] = ['bridge']; // a backchannel is timing, not content: canned at 0ms (a generated one arrived after the micro-pause)
  /** Host TTS on the second channel; resolves when the line finished playing. `false` = the host could not play it. */
  speak: (text: string, kind: LineKind) => Promise<unknown> | boolean | void = () => {};
  /** Host UI (caption / muted line) — never history. */
  show: (text: string, kind: LineKind) => void = () => {};
  /** Stop the in-flight line's audio now (barge-in only). */
  cancel: () => void = () => {};
  onEvent: (e: ChannelEvent) => void = () => {};
  /** Echo cancellation on: backchannels may play while the user speaks. Off (half-duplex) → fillers/bridges in silences only. */
  aec = false;
  decideEveryWords = 3;
  decisionBudgetMs = 300;
  generateBudgetMs = 500;
  minGapMs = 2500;
  /** Backchannel after backchannel needs this gap (v1 Instinct's cadence, ≤12/min). */
  backchannelGapMs = 5000;
  /** A decision whose partial grew by this many words while in flight is stale → dropped. */
  staleWords = 3;
  microWords = 30;
  avoidLines = 4;
  /** Canned picks exclude the last N picks of the same kind. */
  bankExclude = 2;
  backchannelMinWords = 6;
  /** No new interim for this long mid-utterance (a breath / clause gap, shorter than a pause) → a backchannel moment. */
  backchannelPauseMs = 350;
  /** A bridge waits this long after the user's turn is dispatched, and plays only if channel-1 has said nothing yet
   *  (a fast reply/ack needs no bridge — one before it reads as the agent starting a different sentence). 0 = at once. */
  bridgeDelayMs = 1200;
  /** No new interim for this long → a pause (hosts may also call onPause). */
  fillerPauseMs = 700;
  fillerMinWords = 4;
  bridgeMinWords = 5;
  now: () => number = () => performance.now();
  random: () => number = Math.random;
}

const words = (s: string) => (s.trim() ? s.trim().split(/\s+/) : []);

/** Wait for `p` at most `ms`; null when late (the caller's fallback runs; the late answer is ignored). */
function withBudget<T>(p: Promise<T>, ms: number, what: string): Promise<T | null> {
  let t: ReturnType<typeof setTimeout>;
  const late = new Promise<null>((r) => { t = setTimeout(() => { log.debug(`${what}: over the ${ms}ms budget — dropped`); r(null); }, ms); });
  return Promise.race([p.catch((e) => { log.warn(`${what} failed — falling back: ${(e as Error)?.message ?? e}`); return null; }), late]).finally(() => clearTimeout(t));
}

export class SecondChannel {
  private heard = '';
  private userSpeaking = false;
  private main: MainState = 'idle';
  private gen = 0; // bumped by barge-in / a new user turn → in-flight decisions drop
  private decidedAt = 0; // word count at the last decision
  private busy = false;
  private pauseTimer: ReturnType<typeof setTimeout> | null = null;
  private bcTimer: ReturnType<typeof setTimeout> | null = null;
  private bridgeTimer: ReturnType<typeof setTimeout> | null = null;
  private lastAt = -Infinity;
  private lastKind: LineKind | null = null;
  private live: string | null = null; // our last line, until channel-1 audio takes the floor
  private cutLine: string | null = null; // the line the last barge actually cut (spoke())
  private bridged = false; // one bridge per main turn
  private recent: string[] = [];
  private picks = new Map<LineKind, string[]>();
  private inflight: Promise<void> | null = null;
  private readonly startedAt: number;
  private lang: string | null = null;
  private pinned = false;

  public constructor(public options?: Partial<SecondChannelOptions>) {
    this.options = { ...new SecondChannelOptions(), ...options };
    this.startedAt = this.o.now();
    if (this.o.lang) this.setLanguage(this.o.lang, true);
  }

  /** The language lines are spoken in (detected from the user's words, or set by the host). */
  get language(): string | null { return this.lang; }

  /** Switch language: its bank starts loading in the background. `host` = the host's STT-reported language — it pins
   *  the language (later detection never overrides it; only another host call does). Tags normalize to 'he-IL' → 'he'. */
  setLanguage(tag: string, host = false): void {
    if (!tag || (this.pinned && !host)) return;
    if (host) this.pinned = true;
    const lang = primaryLang(tag);
    if (!lang || lang === this.lang) return;
    log.debug(`language ${this.lang} → ${lang}`);
    this.lang = lang;
    this.recent = [];
    this.picks.clear();
    void this.o.banks?.ensure(lang);
  }
  /** Once a language is known, switching needs strong evidence (detectLanguage's `switching`): an English "Um," read as
   *  Portuguese flipped a live session to pt (silence until a pt bank was generated — it then spoke Portuguese). */
  private detect(text: string): void { if (this.pinned) return; const l = detectLanguage(text, !!this.lang); if (l) this.setLanguage(l); }
  private get bank() { return this.lang ? (this.o.banks ? this.o.banks.get(this.lang) : this.lang === 'en' ? EN_BANK : null) : null; }
  private get o(): SecondChannelOptions { return this.options as SecondChannelOptions; }

  /** The user's live partial for the current turn (the whole partial, not a delta). */
  onInterim(text: string): void {
    const t = text.trim();
    if (!t || t === this.heard) return;
    if (this.main !== 'idle') this.onAgentState('idle'); // the user speaks again: a new turn (also after a tool-only turn that never reached audio)
    this.heard = t;
    this.detect(t);
    this.userSpeaking = true;
    if (this.pauseTimer) clearTimeout(this.pauseTimer);
    this.pauseTimer = setTimeout(() => { this.pauseTimer = null; this.onPause(this.o.fillerPauseMs); }, this.o.fillerPauseMs);
    // A breath between clauses (interims stop briefly, the user keeps the floor): the natural backchannel moment. STT
    // partials rarely end on punctuation before the endpoint finalizes the line, so punctuation alone almost never fired.
    if (this.bcTimer) clearTimeout(this.bcTimer);
    this.bcTimer = this.o.aec ? setTimeout(() => { this.bcTimer = null; if (this.userSpeaking && this.heard === t) void this.consider('interim', 0, true); }, this.o.backchannelPauseMs) : null;
    if (words(t).length - this.decidedAt >= this.o.decideEveryWords) void this.consider('interim');
  }

  /** The user went quiet for `ms` (host VAD/STT pause, or the internal interim timer). */
  onPause(ms: number): void {
    if (this.pauseTimer) { clearTimeout(this.pauseTimer); this.pauseTimer = null; }
    if (this.bcTimer) { clearTimeout(this.bcTimer); this.bcTimer = null; }
    this.userSpeaking = false;
    void this.consider('pause', ms);
  }

  /** Channel-1 lifecycle. `queued` = the user's turn was dispatched to the main agent. */
  onAgentState(s: MainState): void {
    if (s === this.main) return;
    this.main = s;
    if (s === 'first_audio' || s === 'speaking') this.live = null;
    if (s !== 'queued' && s !== 'thinking' && this.bridgeTimer) { clearTimeout(this.bridgeTimer); this.bridgeTimer = null; } // channel-1 responded: no bridge
    if (s === 'idle') { this.heard = ''; this.decidedAt = 0; this.bridged = false; return; }
    if (s === 'queued') {
      // The user's turn is final: a pending pause/backchannel moment is stale (never a filler after they finished).
      this.gen++; this.userSpeaking = false; this.bridged = false;
      for (const k of ['pauseTimer', 'bcTimer', 'bridgeTimer'] as const) { if (this[k]) clearTimeout(this[k]!); this[k] = null; }
      if (this.o.bridgeDelayMs > 0) this.bridgeTimer = setTimeout(() => { this.bridgeTimer = null; void this.consider('working'); }, this.o.bridgeDelayMs);
      else void this.consider('working');
    }
  }

  /** The user's final line was dispatched to the main agent (its words are the bridge's micro-context). */
  onUserTurn(text: string): void {
    this.heard = text.trim();
    this.detect(this.heard);
    this.main = 'idle';
    this.onAgentState('queued');
  }

  /** The main answer's streamed text (before its audio): channel-1 is responding — no bridge from here on. */
  onMainTokens(text: string): void {
    if (text.trim() && (this.main === 'queued' || this.main === 'thinking')) this.onAgentState('streaming_text');
  }

  /** The user barged in: cut the in-flight line now and drop every pending decision. */
  onBargeIn(): void {
    this.gen++;
    this.cutLine = this.inflight || this.live ? this.live : null;
    if (this.inflight) { this.inflight = null; try { this.o.cancel(); } catch (e) { log.warn(`cancel threw: ${(e as Error).message}`); } }
  }

  /** Is `text` the line the last barge-in cut (ours, still holding the floor)? Hosts strip it from the cut reply — it is
   *  not the agent's speech. By identity, not by text: a real reply that merely repeats a recent line is kept. Consumed once. */
  spoke(text: string): boolean { const c = this.cutLine; this.cutLine = null; return !!c && c === text.trim(); }

  /** Resolves when the in-flight line finished playing (immediately when none) — hosts enqueue channel-1 audio behind it. */
  drain(): Promise<void> { return this.inflight ?? Promise.resolve(); }

  private get audioLocked(): boolean { return this.main === 'first_audio' || this.main === 'speaking'; }
  private tail(): string { return words(this.heard).slice(-this.o.microWords).join(' '); }

  /** Hard gates every line passes, whoever proposed it. */
  private allowed(k: LineKind): boolean {
    if (this.audioLocked || this.inflight || this.o.now() - this.lastAt < this.o.minGapMs || (k === this.lastKind && (k !== 'backchannel' || this.o.now() - this.lastAt < this.o.backchannelGapMs))) return false; // backchannels may repeat (spaced by backchannelGapMs) across a long turn; lines never do (pick/line)
    if (k === 'backchannel') return this.o.aec && this.userSpeaking && this.main === 'idle';
    if (k === 'filler') return !this.userSpeaking && this.main === 'idle';
    return (this.main === 'queued' || this.main === 'thinking') && !this.bridged; // bridge: only while channel-1 has said nothing
  }

  /** 0ms rule: the candidate line for this trigger, or silence. */
  private rule(trigger: Trigger, pauseMs = 0, breath = false): ChannelKind {
    const w = words(this.heard);
    if (!this.lang) return 'silence'; // language not confidently known → never guess (no English to a Spanish speaker)
    if (w.length <= 3 || (w.length <= 6 && isPhatic(this.heard, this.bank))) return 'silence'; // greeting / quick turn bias, in the user's language
    const k: LineKind | null =
      trigger === 'interim' ? (this.backchannelMoment(w.length, breath) ? 'backchannel' : null)
        : trigger === 'pause' ? (pauseMs >= this.o.fillerPauseMs && w.length >= this.o.fillerMinWords ? 'filler' : null)
          : w.length >= this.o.bridgeMinWords ? 'bridge' : null;
    return k && this.allowed(k) ? k : 'silence';
  }

  /** A clause boundary mid-floor: a breath or a comma-like close. Not a question (it wants the answer, not "mm-hm"), and a
   *  sentence end only deep into a story — a short request's end is the end of their turn (replay: "mm-hm" right after
   *  "Check hacker news for recent news.", just before the final). */
  private backchannelMoment(n: number, breath: boolean): boolean {
    if (n < this.o.backchannelMinWords || /\?$/.test(this.heard)) return false;
    if (/[.!]$/.test(this.heard)) return n >= 2 * this.o.backchannelMinWords;
    return breath || /[,;:…]$/.test(this.heard);
  }

  private async consider(trigger: Trigger, pauseMs?: number, breath = false): Promise<void> {
    if (this.busy) return;
    const n0 = words(this.heard).length;
    this.decidedAt = n0;
    const rule = this.rule(trigger, pauseMs, breath);
    if (rule === 'silence') return;
    this.busy = true;
    const gen = this.gen, t0 = this.o.now();
    const ev = (e: Partial<ChannelEvent> & Pick<ChannelEvent, 'acted'>) => this.o.onEvent({ trigger, rule, ms: Math.round(this.o.now() - t0), ...e });
    const stale = () => gen !== this.gen || words(this.heard).length - n0 >= this.o.staleWords;
    try {
      let kind: ChannelKind = rule;
      let choice: string | undefined;
      if (this.o.decide) {
        const d = await withBudget(this.o.decide(this.tail(), this.state(pauseMs)), this.o.decisionBudgetMs, 'channel decision');
        choice = d?.choice;
        if (choice && choice in CHANNEL_ACTIONS) kind = choice as ChannelKind; // in budget: the refiner wins (incl. a veto)
      }
      if (kind === 'silence') return ev({ choice, acted: 'silence' });
      if (stale()) return ev({ choice, kind, acted: 'stale' });
      if (!this.allowed(kind)) return ev({ choice, kind, acted: 'gated' });
      const text = await this.line(kind);
      if (!text) return ev({ choice, kind, acted: 'no_bank' }); // no bank in the user's language yet → silence, never English
      if (stale()) return ev({ choice, kind, text, acted: 'stale' });
      if (!this.allowed(kind)) return ev({ choice, kind, text, acted: 'gated' }); // channel-1 audio started meanwhile
      ev({ choice, kind, text, acted: this.say(kind, text) ? 'spoken' : 'skipped' });
    } finally {
      this.busy = false;
    }
  }

  private state(pauseMs?: number): Record<string, unknown> {
    const now = this.o.now();
    return {
      user_speaking: this.userSpeaking,
      pause_ms: pauseMs ?? null,
      agent_state: this.main,
      last_line: this.recent.at(-1) ?? null,
      ms_since_last_line: Number.isFinite(this.lastAt) ? Math.round(now - this.lastAt) : null,
      call_ms: Math.round(now - this.startedAt),
    };
  }

  private async line(kind: LineKind): Promise<string> {
    if (this.o.generate && this.o.generateFor.includes(kind)) {
      const ac = new AbortController();
      const ctx: ChannelContext = { kind, heard: this.tail(), state: this.main, avoid: [...this.recent], lang: this.lang! };
      const raw = await withBudget(this.o.generate(ctx, ac.signal), this.o.generateBudgetMs, `${kind} generate`);
      if (raw === null) ac.abort();
      const text = cleanLine(raw ?? '', this.lang!);
      if (text && !this.recent.some((r) => norm(r) === norm(text))) return text;
      if (text) log.debug(`generated "${text}" repeats a recent line — canned`);
    }
    return this.pick(kind);
  }

  /** Canned rotation in the user's language: never one of the last `bankExclude` picks of this kind, nor a recent
   *  line. '' = no bank for this language yet (silence). */
  private pick(kind: LineKind): string {
    const pool = this.bank?.[kind] ?? [];
    if (!pool.length) return '';
    const hist = this.picks.get(kind) ?? [];
    const recent = new Set([...hist.slice(-this.o.bankExclude), ...this.recent].map(norm));
    const fresh = pool.filter((x) => !recent.has(norm(x)));
    const from = fresh.length ? fresh : pool;
    const p = from[Math.floor(this.o.random() * from.length)];
    this.picks.set(kind, [...hist, p].slice(-this.o.bankExclude));
    return p;
  }

  private say(kind: LineKind, text: string): boolean {
    let r: ReturnType<SecondChannelOptions['speak']>;
    try { r = this.o.speak(text, kind); } catch (e) { log.warn(`speak threw — line dropped: ${(e as Error).message}`); return false; }
    if (r === false) return false;
    this.lastAt = this.o.now();
    this.lastKind = kind;
    if (kind === 'bridge') this.bridged = true;
    this.recent = [...this.recent, text].slice(-this.o.avoidLines);
    this.live = text.trim();
    this.o.show(text, kind);
    const p: Promise<void> = Promise.resolve(r)
      .then(() => {}, (e) => { log.warn(`channel line playback failed: ${(e as Error)?.message ?? e}`); })
      .finally(() => { if (this.inflight === p) this.inflight = null; });
    this.inflight = p;
    return true;
  }
}

const KIND_HINT: Record<LineKind, string> = {
  backchannel: 'a tiny listener cue while they keep talking ("mm-hm", "right", "yeah") — shows you follow, never takes the turn',
  filler: 'a soft listener sound while THEY search for words ("mm…", "mhm", "yeah…") — never a word that continues their sentence or starts yours',
  bridge: 'a neutral one-moment cue while the answer is prepared ("let me see…", "one sec…") — never the start of a statement or opinion ("the thing is", "well actually")',
};

/** A fast-model generator with a micro-context prompt (no history, no system bloat). */
export function fastLineGenerator(ai: ChatLike, model: string): NonNullable<SecondChannelOptions['generate']> {
  return async (c, signal) => {
    const prompt = `You voice the tiny side-sounds of a live spoken conversation. Write ONE line of 1-5 words: ${KIND_HINT[c.kind]}.\n` +
      `The user just said: "${c.heard}"\nThe assistant is: ${c.state}.\n` +
      (c.avoid.length ? `Do not reuse any of: ${c.avoid.map((a) => `"${a}"`).join(', ')}.\n` : '') +
      `React to their actual words, casually, in ${languageName(c.lang)}. Output only the line.`;
    // gpt-oss (the default reflex) is a reasoning model: at a tiny maxTokens its REASONING came back as the line
    // ("The user wants: …"); low effort + room for it measured ~200ms p50 on groq with clean lines.
    const reasoning = /gpt-oss/.test(model) ? { providerOptions: { reasoning_effort: 'low' } } : {};
    const r = (await ai.chat({ model, messages: [{ role: 'user', content: prompt }], stream: false, signal, maxTokens: 150, ...reasoning })) as ChatResponse;
    return r.content ?? '';
  };
}
